/**
 * SingleSimulator — Standalone single-agent simulator.
 * Supabase-native: no Telerivet. Creates a synthetic contact on first message,
 * runs agent.execute() with integration payload (agentfactory saves messages + evals).
 */

import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Skeleton } from "@/components/ui/skeleton"
import { Loader2, Send, RefreshCw, MessageSquare, Bot, User, AlertTriangle, ChevronLeft, ChevronRight, FlaskConical } from "lucide-react"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import { useToast } from "@/hooks/use-toast"
import { useSupabaseSimulation, useEvalItemMap, useAgentFlags } from "../hooks"
import { QuickReplies } from "./QuickReplies"
import { extractQuickReplies } from "../utils"
import { MessageBubble } from "@/pages/dev/moderation/components/MessageBubble"
import { useMessages, useFlags, useRatings, useComments } from "@/pages/dev/moderation/hooks"
import type { FlagType, DetectedItem, MessageComment, ModerationMessage } from "@/pages/dev/moderation/types"
import { messageHasActiveModerationFlags, isBotMessage } from "@/pages/dev/moderation/utils/conversationUtils"
import { supabase } from "@/lib/data"
interface SingleSimulatorProps {
  agentId?: string
  teamId?: string
  /**
   * Seed the simulator with an existing conversation (synthetic contact) so the
   * historical thread loads and the user can continue acting as the contact.
   * When provided, no new contact is created on mount.
   */
  initialContactId?: string
  /**
   * Optional content rendered at the start of the top bar (e.g. back buttons +
   * contact name when continuing a conversation). Lets the caller put its
   * controls on the same line as the simulator's own controls.
   */
  headerSlot?: React.ReactNode
}

export function SingleSimulator({ agentId, teamId, initialContactId, headerSlot }: SingleSimulatorProps = {}) {
  const [label, setLabel] = useState("")
  const [message, setMessage] = useState("")
  const [contactId, setContactId] = useState<string | null>(initialContactId || null)
  const [isSending, setIsSending] = useState(false)
  const [isEvaluating, setIsEvaluating] = useState(false)
  const [isEscalated, setIsEscalated] = useState(false)
  const [comments, setComments] = useState<MessageComment[]>([])
  const [flaggedIndex, setFlaggedIndex] = useState(0)

  const messageRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const scrollRef = useRef<HTMLDivElement>(null)
  const { toast } = useToast()

  const { sendSimulatedMessage, ensureContact } = useSupabaseSimulation()
  const evalItemMap = useEvalItemMap(teamId)
  const agentFlags = useAgentFlags(agentId)
  const { messages, loading: loadingMessages, refresh: refreshMessages, contact: liveContact } = useMessages(contactId)
  const { toggleFlag, resolveAllFlags } = useFlags()
  const { submitRating } = useRatings()
  const { getComments, addComment, deleteComment, getCommentsForMessage } = useComments(contactId || "")

  // Sync escalation from polled contact
  useEffect(() => {
    if (typeof liveContact?.hitl === "boolean") {
      if (liveContact.hitl !== isEscalated) setIsEscalated(liveContact.hitl)
      return
    }
    if (liveContact?.moderation_data) {
      try {
        const d = JSON.parse(liveContact.moderation_data as string)
        const isHuman = d?.handled_by === "human"
        if (isHuman !== isEscalated) setIsEscalated(isHuman)
      } catch {}
    }
  }, [liveContact?.hitl, liveContact?.moderation_data, isEscalated])

  // When seeded with an existing conversation, adopt it and normalize the
  // synthetic contact (backfills legacy contacts) so it loads cleanly.
  useEffect(() => {
    if (!initialContactId || !teamId) return
    setContactId(initialContactId)
    ensureContact({ contactId: initialContactId, teamId }).catch(() => {
      /* normalization is best-effort; messages still load from contactId */
    })
  }, [initialContactId, teamId, ensureContact])

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      const viewport = scrollRef.current.querySelector("[data-slot=scroll-area-viewport]") as HTMLElement
      const target = viewport || scrollRef.current
      target.scrollTop = target.scrollHeight
    }
  }, [messages, isSending])

  // Poll every 5s
  useEffect(() => {
    if (!contactId) return
    const interval = setInterval(() => refreshMessages(), 5000)
    return () => clearInterval(interval)
  }, [contactId, refreshMessages])

  // Load comments when contactId changes
  useEffect(() => {
    if (!contactId) { setComments([]); return }
    getComments().then(setComments).catch(() => setComments([]))
  }, [contactId, getComments])

  // Flagged messages
  const flaggedMessages = useMemo(
    () => messages.filter(m => messageHasActiveModerationFlags(m as ModerationMessage, evalItemMap)),
    [messages, evalItemMap]
  )

  useEffect(() => {
    setFlaggedIndex(i => Math.min(i, Math.max(0, flaggedMessages.length - 1)))
  }, [flaggedMessages.length])

  const scrollToFlagged = useCallback((idx: number) => {
    const msg = flaggedMessages[idx]
    if (!msg) return
    messageRefs.current[msg.id]?.scrollIntoView({ behavior: "smooth", block: "center" })
  }, [flaggedMessages])

  // Quick replies parsed from the latest bot message's [bracketed] options —
  // same mechanism as the agent flow chat panel.
  const quickReplies = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i] as ModerationMessage
      if (isBotMessage(msg)) return extractQuickReplies(msg.message)
      if (msg.role === "user") break
    }
    return []
  }, [messages])

  const handleToggleEscalation = async () => {
    if (!contactId) return
    const newMode = isEscalated ? "bot" : "human"
    try {
      const current = liveContact?.moderation_data
        ? (() => { try { return JSON.parse(liveContact.moderation_data as string) } catch { return {} } })()
        : {}
      await supabase
        .from("contacts")
        .update({
          moderation_data: JSON.stringify({ ...current, handled_by: newMode }),
          hitl: newMode === "human",
        } as any)
        .eq("id", contactId)
      setIsEscalated(!isEscalated)
    } catch {
      /* toggle failed silently */
    }
  }

  const handleNewConversation = async () => {
    if (!teamId) return
    setContactId(null)
    setIsEscalated(false)
    setComments([])
    setFlaggedIndex(0)
    // Create a fresh synthetic contact for the next conversation
    const newId = await ensureContact({ contactId: null, teamId, label: label || undefined })
    setContactId(newId)
  }

  const handleSend = async (override?: string) => {
    const raw = override ?? message
    if (!raw.trim() || !teamId || !agentId || isSending) return

    const messageToSend = raw.trim()
    if (override === undefined) setMessage("")
    setIsSending(true)

    try {
      const result = await sendSimulatedMessage({
        contactId,
        teamId,
        agentId,
        message: messageToSend,
        label: label || undefined,
        // Refresh as soon as the user message is written — before agent responds
        onMessageInserted: (newId) => {
          if (newId !== contactId) setContactId(newId)
          refreshMessages()
        },
      })

      if (result.newContactId && result.newContactId !== contactId) {
        setContactId(result.newContactId)
      }

      if (!result.success) throw new Error(result.error || "Agent execution failed")

      refreshMessages()

      if (result.evalPromise) {
        setIsEvaluating(true)
        result.evalPromise
          .then(() => refreshMessages())
          .finally(() => setIsEvaluating(false))
      }
    } catch (err) {
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to send message",
        variant: "destructive",
      })
    } finally {
      setIsSending(false)
    }
  }

  const handleToggleFlag = async (messageId: string, flagType: FlagType, currentItems: DetectedItem[], currentFlags?: FlagDetectedItem[]) => {
    try {
      await toggleFlag(messageId, flagType, currentItems, currentFlags)
      await refreshMessages()
    } catch {
      /* toggle failed silently */
    }
  }

  const handleResolveAll = async (messageId: string, currentItems?: DetectedItem[], currentFlags?: FlagDetectedItem[]) => {
    const msg = messages.find(m => m.id === messageId)
    if (!msg) return
    try {
      await resolveAllFlags(messageId, currentItems || msg.user_detected_items || [], currentFlags || msg.custom_message_flags || [])
      await refreshMessages()
    } catch {
      /* resolve failed silently */
    }
  }

  const handleAddComment = useCallback(async (messageId: string, text: string) => {
    if (!contactId) return
    const updated = await addComment(text, "simulator", "Simulator", messageId)
    setComments(updated)
  }, [contactId, addComment])

  const handleDeleteComment = useCallback(async (commentId: string) => {
    if (!contactId) return
    const updated = await deleteComment(commentId)
    setComments(updated)
  }, [contactId, deleteComment])

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Top bar */}
      <div className="shrink-0 flex items-center gap-2 p-2 border-b">
        {/* Caller-provided header content (e.g. back buttons + name) */}
        {headerSlot && <div className="flex items-center gap-2 min-w-0 flex-1">{headerSlot}</div>}

        {/* Label + New conversation — only when not continuing an existing
            conversation (a seeded contact already has its own identity). */}
        {!initialContactId && (
          <>
            <Label htmlFor="label" className="text-xs whitespace-nowrap text-muted-foreground">User:</Label>
            <Input
              id="label"
              placeholder="Simulated user label (optional)"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="h-7 text-xs flex-1 min-w-[120px]"
            />

            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs gap-1 shrink-0"
              onClick={handleNewConversation}
              disabled={!teamId || isSending}
              title="Start a fresh conversation with a new synthetic contact"
            >
              <RefreshCw className="h-3 w-3" />
              New
            </Button>
          </>
        )}

        {/* Spacer so the right-aligned controls stay put when nothing on the
            left grows to fill the row */}
        {initialContactId && !headerSlot && <div className="flex-1" />}

        {/* Flagged navigator */}
        {flaggedMessages.length > 0 && (
          <div className="flex items-center gap-1 shrink-0 border rounded px-2 py-1 bg-amber-50 dark:bg-amber-950/20 border-amber-300">
            <AlertTriangle className="h-3 w-3 text-amber-500" />
            <span className="text-[11px] text-amber-700 dark:text-amber-400 font-medium">
              {flaggedIndex + 1}/{flaggedMessages.length}
            </span>
            <Button variant="ghost" size="icon" className="h-4 w-4"
              onClick={() => { const i = Math.max(0, flaggedIndex - 1); setFlaggedIndex(i); scrollToFlagged(i) }}
              disabled={flaggedIndex === 0}
            ><ChevronLeft className="h-3 w-3" /></Button>
            <Button variant="ghost" size="icon" className="h-4 w-4"
              onClick={() => { const i = Math.min(flaggedMessages.length - 1, flaggedIndex + 1); setFlaggedIndex(i); scrollToFlagged(i) }}
              disabled={flaggedIndex === flaggedMessages.length - 1}
            ><ChevronRight className="h-3 w-3" /></Button>
          </div>
        )}

        {/* Bot / Human toggle */}
        {contactId && (
          <div className={cn(
            "flex items-center gap-1.5 border rounded px-2 py-1 shrink-0",
            isEscalated && "border-red-500 bg-red-50 dark:bg-red-950/20"
          )}>
            <Bot className={cn("h-3 w-3", !isEscalated ? "text-primary" : "text-muted-foreground/30")} />
            <Switch checked={isEscalated} onCheckedChange={handleToggleEscalation} />
            <User className={cn("h-3 w-3", isEscalated ? "text-red-500" : "text-muted-foreground/30")} />
          </div>
        )}
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 min-h-0 p-4" ref={scrollRef}>
        {!contactId && !loadingMessages ? (
          <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
            <MessageSquare className="h-8 w-8 mb-2 opacity-30" />
            <p className="text-sm">Send a test message to start a conversation</p>
            <p className="text-xs text-muted-foreground/60 mt-1">A synthetic contact will be created in Supabase</p>
          </div>
        ) : loadingMessages && messages.length === 0 ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-3/4" />)}
          </div>
        ) : (
          <div className="pt-1">
            {messages.map((msg) => (
              <div key={msg.id} ref={(el) => { messageRefs.current[msg.id] = el }}>
                <MessageBubble
                  message={msg}
                  evalItems={evalItemMap}
                  agentFlags={agentFlags}
                  onToggleFlag={(flagType, currentItems, currentFlags) => handleToggleFlag(msg.id, flagType, currentItems, currentFlags)}
                  onResolveAll={(currentItems, currentFlags) => handleResolveAll(msg.id, currentItems, currentFlags)}
                  onRate={(rating, example) => submitRating(msg.id, rating, example)}
                  comments={getCommentsForMessage(comments, msg.id)}
                  onAddComment={(text) => handleAddComment(msg.id, text)}
                  onDeleteComment={handleDeleteComment}
                />
              </div>
            ))}

            {isSending && (
              <div className="flex justify-start mb-2 mx-1">
                <div className="bg-muted rounded-lg px-3 py-2 ml-1">
                  <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Running agent…
                  </div>
                </div>
              </div>
            )}
            {!isSending && isEvaluating && (
              <div className="flex justify-end mb-1 mr-1">
                <div className="flex items-center gap-1.5 text-[11px] text-violet-600 bg-violet-50 border border-violet-200 rounded-full px-2.5 py-1">
                  <FlaskConical className="h-3 w-3 animate-pulse shrink-0" />
                  Running evaluations…
                </div>
              </div>
            )}

            {!isSending && !isEscalated && (
              <QuickReplies
                replies={quickReplies}
                onSelect={(reply) => handleSend(reply)}
                disabled={!agentId || isSending}
              />
            )}
          </div>
        )}
      </ScrollArea>

      {/* Composer */}
      <div className="shrink-0 p-2 border-t">
        {isEscalated && (
          <p className="text-[10px] text-red-500 mb-1 px-1">Human mode — agent will not respond</p>
        )}
        <div className="flex gap-1.5">
          <Textarea
            placeholder={agentId ? "Type your test message…" : "Select an agent first"}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault()
                handleSend()
              }
            }}
            className="flex-1 text-sm resize-none min-h-[36px]"
            rows={1}
            disabled={!agentId || isSending}
          />
          <Button
            onClick={() => handleSend()}
            disabled={isSending || !agentId || !message.trim()}
            className="h-9 w-9 shrink-0"
            size="icon"
          >
            {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  )
}
